if (!customElements.get('footer-disclosure')) {
  customElements.define('footer-disclosure', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.body = this.querySelector('.footer-menu__body');
      if (!this.details || !this.summary || !this.body) return;
      this.desktop = matchMedia('(min-width: 750px)');
      this.syncMode();
      this.desktop.addEventListener('change', () => this.syncMode(), { signal: this.controller.signal });
      this.summary.addEventListener('click', event => {
        if (this.desktop.matches) return;
        event.preventDefault();
        this.details.open ? this.close() : this.open();
      }, { signal: this.controller.signal });
    }

    get reducedMotion() { return matchMedia('(prefers-reduced-motion: reduce)').matches; }
    syncMode() { this.animation?.cancel(); this.details.open = this.desktop.matches; }
    open() {
      this.animation?.cancel();
      this.details.open = true;
      if (this.reducedMotion) return;
      this.animation = this.body.animate([
        { height: '0px', opacity: 0, transform: 'translateY(-3px)' },
        { height: `${this.body.scrollHeight}px`, opacity: 1, transform: 'translateY(0)' }
      ], { duration: 210, easing: 'cubic-bezier(.22, 1, .36, 1)' });
    }
    close() {
      this.animation?.cancel();
      if (this.reducedMotion) { this.details.open = false; return; }
      this.animation = this.body.animate([
        { height: `${this.body.offsetHeight}px`, opacity: 1 },
        { height: '0px', opacity: 0 }
      ], { duration: 145, easing: 'cubic-bezier(.4, 0, 1, 1)' });
      this.animation.finished.then(() => { this.details.open = false; }).catch(() => {});
    }
    disconnectedCallback() { this.animation?.cancel(); this.controller?.abort(); }
  });
}

if (!customElements.get('theme-footer')) {
  customElements.define('theme-footer', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.slider = this.querySelector('[data-reassurance-slider]');
      this.track = this.querySelector('[data-reassurance-track]');
      this.slides = [...this.querySelectorAll('[data-reassurance-slide]')];
      this.dots = [...(this.slider?.querySelectorAll('[data-slider-dot]') || [])];
      this.index = 0;
      this.signature = this.querySelector('.footer-signature');
      this.signatureText = this.querySelector('.footer-signature__text');
      this.background = this.querySelector('[data-footer-background]');
      this.backgroundMedia = this.background?.querySelector('.footer__background-media');
      this.setupSignature();
      this.setupBackground();
      if (!this.slider || this.slides.length < 2) return;
      const { signal } = this.controller;
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => { this.goTo(index); this.restart(); }, { signal }));
      this.slider.addEventListener('pointerenter', () => this.stop(), { signal });
      this.slider.addEventListener('pointerleave', () => this.start(), { signal });
      this.slider.addEventListener('focusin', () => this.stop(), { signal });
      this.slider.addEventListener('focusout', event => { if (!this.slider.contains(event.relatedTarget)) this.start(); }, { signal });
      document.addEventListener('visibilitychange', () => document.hidden ? this.stop() : this.start(), { signal });
      document.addEventListener('shopify:block:select', event => {
        const slide = event.target.closest?.('[data-reassurance-slide]');
        if (slide && this.contains(slide)) { this.stop(); this.goTo(this.slides.indexOf(slide)); }
      }, { signal });
      this.goTo(0);
      this.start();
    }

    get mobile() { return matchMedia('(max-width: 749px)').matches; }
    get reducedMotion() { return matchMedia('(prefers-reduced-motion: reduce)').matches; }
    setupSignature() {
      if (!this.signature || !this.signatureText) return;
      this.signatureObserver?.disconnect();
      this.signatureObserver = new ResizeObserver(() => this.fitSignature());
      this.signatureObserver.observe(this.signature);
      this.fitSignature();
      document.fonts?.ready.then(() => this.fitSignature()).catch(() => {});
    }
    fitSignature() {
      if (!this.signature || !this.signatureText) return;
      this.signatureText.style.removeProperty('--footer-signature-fitted-size');
      const signatureStyles = getComputedStyle(this.signature);
      const availableWidth = this.signature.clientWidth
        - parseFloat(signatureStyles.paddingLeft)
        - parseFloat(signatureStyles.paddingRight);
      const textWidth = this.signatureText.scrollWidth;
      const baseFontSize = parseFloat(getComputedStyle(this.signatureText).fontSize);
      if (availableWidth > 0 && textWidth > 0) {
        this.signatureText.style.setProperty('--footer-signature-fitted-size', `${baseFontSize * availableWidth / textWidth}px`);
      }
    }
    setupBackground() {
      this.backgroundObserver?.disconnect();
      if (this.backgroundFrame) cancelAnimationFrame(this.backgroundFrame);
      if (!this.background || !this.backgroundMedia || this.background.dataset.parallax !== 'true' || this.reducedMotion) {
        this.style.setProperty('--footer-parallax-y', '0px');
        return;
      }

      const { signal } = this.controller;
      this.backgroundIsVisible = true;
      window.addEventListener('scroll', () => this.queueBackgroundUpdate(), { passive: true, signal });
      window.addEventListener('resize', () => this.queueBackgroundUpdate(), { passive: true, signal });

      if ('IntersectionObserver' in window) {
        this.backgroundObserver = new IntersectionObserver(entries => {
          this.backgroundIsVisible = entries.some(entry => entry.isIntersecting);
          if (this.backgroundIsVisible) this.queueBackgroundUpdate();
        }, { rootMargin: '50% 0px' });
        this.backgroundObserver.observe(this);
      }
      this.queueBackgroundUpdate();
    }
    queueBackgroundUpdate() {
      if (!this.backgroundIsVisible || this.backgroundFrame) return;
      this.backgroundFrame = requestAnimationFrame(() => {
        this.backgroundFrame = null;
        const rect = this.getBoundingClientRect();
        const travel = Math.max(1, window.innerHeight + rect.height);
        const progress = Math.min(1, Math.max(0, (window.innerHeight - rect.top) / travel));
        const strength = Number(this.background.dataset.parallaxStrength) || 60;
        this.style.setProperty('--footer-parallax-y', `${((progress - .5) * strength).toFixed(2)}px`);
      });
    }
    goTo(index) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.track.style.transform = this.mobile ? `translate3d(-${this.index * 100}%, 0, 0)` : '';
      this.dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', dotIndex === this.index ? 'true' : 'false'));
    }
    start() {
      this.stop();
      if (!this.mobile || this.reducedMotion || this.slider.dataset.autoplay !== 'true' || document.hidden) return;
      this.timer = setInterval(() => this.goTo(this.index + 1), Number(this.slider.dataset.interval) || 5000);
    }
    stop() { clearInterval(this.timer); }
    restart() { this.stop(); this.start(); }
    disconnectedCallback() {
      this.stop();
      this.signatureObserver?.disconnect();
      this.backgroundObserver?.disconnect();
      if (this.backgroundFrame) cancelAnimationFrame(this.backgroundFrame);
      this.controller?.abort();
    }
  });
}
